// Display-name maps + ordered option lists for the property enums that
// the create / edit wizard renders as chip pickers and selects.
//
// The wire types come from the OpenAPI codegen (PropertyType, etc.) as
// string-literal unions. Here we annotate each value with a Vietnamese
// label and a stable presentation order matching the design comp.

import type { components } from "@/types/api";

export type PropertyType = NonNullable<
  components["schemas"]["PropertyCreateRequest"]["propertyType"]
>;
export type PropertyDirection = NonNullable<
  components["schemas"]["PropertyCreateRequest"]["direction"]
>;
export type PropertyFurniture = NonNullable<
  components["schemas"]["PropertyCreateRequest"]["furniture"]
>;
export type BrokerageFeeType = NonNullable<
  components["schemas"]["PropertyCreateRequest"]["brokerageFeeType"]
>;

interface Option<T extends string> {
  value: T;
  label: string;
}

// Order matches design/src/components/desktop/PropertyCreate.jsx
// (Căn hộ first since apartments dominate Hanoi listings).
export const PROPERTY_TYPE_OPTIONS: readonly Option<PropertyType>[] = [
  { value: "APARTMENT", label: "Căn hộ" },
  { value: "VILLA", label: "Biệt thự" },
  { value: "TOWNHOUSE", label: "Liền kề" },
  { value: "OFFICETEL", label: "Officetel" },
  { value: "SHOPHOUSE", label: "Shophouse" },
  { value: "LAND", label: "Đất nền" },
];

// 4 cardinal directions then 4 intercardinals, both groups in
// Đông→Bắc order. Feng-shui convention in VN.
export const PROPERTY_DIRECTION_OPTIONS: readonly Option<PropertyDirection>[] = [
  { value: "EAST", label: "Đông" },
  { value: "WEST", label: "Tây" },
  { value: "SOUTH", label: "Nam" },
  { value: "NORTH", label: "Bắc" },
  { value: "SOUTHEAST", label: "Đông Nam" },
  { value: "SOUTHWEST", label: "Tây Nam" },
  { value: "NORTHEAST", label: "Đông Bắc" },
  { value: "NORTHWEST", label: "Tây Bắc" },
];

export const PROPERTY_FURNITURE_OPTIONS: readonly Option<PropertyFurniture>[] = [
  { value: "FULL", label: "Đầy đủ" },
  { value: "BASIC", label: "Cơ bản" },
  { value: "BARE", label: "Bàn giao thô" },
];

// Brokerage-fee unit: months of rent, a percent of price, or a fixed amount.
export const BROKERAGE_FEE_TYPE_OPTIONS: readonly Option<BrokerageFeeType>[] = [
  { value: "MONTHS", label: "Số tháng" },
  { value: "PERCENT", label: "Phần trăm" },
  { value: "AMOUNT", label: "Số tiền" },
];

// ---------------------------------------------------------------------------
// Label lookups (small constants generated from the option arrays so
// callers can do `propertyTypeLabel(value)` without a Find)
// ---------------------------------------------------------------------------

const PROPERTY_TYPE_LABELS = Object.fromEntries(
  PROPERTY_TYPE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<PropertyType, string>;

const PROPERTY_DIRECTION_LABELS = Object.fromEntries(
  PROPERTY_DIRECTION_OPTIONS.map((o) => [o.value, o.label]),
) as Record<PropertyDirection, string>;

const PROPERTY_FURNITURE_LABELS = Object.fromEntries(
  PROPERTY_FURNITURE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<PropertyFurniture, string>;

export function propertyTypeLabel(value: PropertyType | null | undefined): string {
  if (value == null) return "";
  return PROPERTY_TYPE_LABELS[value] ?? value;
}

export function propertyDirectionLabel(value: PropertyDirection | null | undefined): string {
  if (value == null) return "";
  return PROPERTY_DIRECTION_LABELS[value] ?? value;
}

export function propertyFurnitureLabel(value: PropertyFurniture | null | undefined): string {
  if (value == null) return "";
  return PROPERTY_FURNITURE_LABELS[value] ?? value;
}

// ---------------------------------------------------------------------------
// Bedroom picker — Studio / 1 / 2 / 3 / 4+
// ---------------------------------------------------------------------------

/**
 * Bedroom button options. Studio maps to 0 bedrooms; "4+" represents any
 * count ≥ 4 (display only — the stored value is 4 for now until we have
 * a real "minBedrooms" filter).
 */
export const BEDROOM_OPTIONS: ReadonlyArray<{ label: string; value: number }> = [
  { label: "Studio", value: 0 },
  { label: "1", value: 1 },
  { label: "1.5", value: 1.5 },
  { label: "2", value: 2 },
  { label: "2.5", value: 2.5 },
  { label: "3", value: 3 },
  { label: "3.5", value: 3.5 },
  { label: "4+", value: 4 },
];

/** Match a numeric value to one of {@link BEDROOM_OPTIONS}. */
export function bedroomLabel(value: number | null | undefined): string {
  if (value == null) return "";
  const exact = BEDROOM_OPTIONS.find((o) => o.value === value);
  if (exact) return exact.label;
  // Values higher than 4 collapse into "4+".
  return value >= 4 ? "4+" : String(value);
}
